<template>
  <div class="step-whitespace">
    <v-container fluid class="pa-6 px-4">
      <v-row justify="center">
        <v-col cols="12" lg="11">
          <div v-if="!issues || issues.length === 0" class="text-center py-8">
            <v-icon size="64" color="success" class="mb-4">mdi-check-circle</v-icon>
            <div class="text-h6">No formatting issues found</div>
            <div class="text-body-2 grey--text">All text is clean and tidy</div>
          </div>
          
          <div v-else>
            <div class="text-body-1 mb-2 mt-2">
              We found <strong>{{ issues.length }}</strong> value{{ issues.length > 1 ? 's' : '' }} 
              with formatting issues that will cause problems when importing your assets.
            </div>
            <div class="text-body-2 grey--text mb-2">
              Click "Fix All" to clean these up automatically.
            </div>
            <div class="d-flex mb-8">
              <v-btn 
                color="primary" 
                :disabled="isApplied"
                @click="applyFixes"
                large
              >
                <v-icon left>mdi-auto-fix</v-icon>
                {{ isApplied ? 'Fixed!' : 'Fix All' }}
              </v-btn>
            </div>
            
            <v-row class="mb-4">
              <v-col cols="12" sm="6" v-if="lineBreakCount > 0">
                <v-card outlined class="issue-summary-card">
                  <v-card-text class="d-flex align-center pa-4">
                    <div class="issue-icon issue-icon--linebreak mr-4">
                      <v-icon color="white" size="28">mdi-keyboard-return</v-icon>
                    </div>
                    <div>
                      <div class="text-h5 font-weight-bold">{{ lineBreakCount }}</div>
                      <div class="text-body-2 grey--text">Line break{{ lineBreakCount > 1 ? 's' : '' }} found</div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              
              <v-col cols="12" sm="6" v-if="extraSpaceCount > 0">
                <v-card outlined class="issue-summary-card">
                  <v-card-text class="d-flex align-center pa-4">
                    <div class="issue-icon issue-icon--space mr-4">
                      <v-icon color="white" size="28">mdi-keyboard-space</v-icon>
                    </div>
                    <div>
                      <div class="text-h5 font-weight-bold">{{ extraSpaceCount }}</div>
                      <div class="text-body-2 grey--text">Extra space{{ extraSpaceCount > 1 ? 's' : '' }} found</div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            
            <div class="text-h6 font-weight-medium mb-4 mt-6">
              Issues ({{ issues.length }})
            </div>
            <v-card outlined class="mb-4">
              <div class="comparison-header">
                <div class="comparison-header-cell comparison-header-cell--info">Location</div>
                <div class="comparison-header-cell comparison-header-cell--before">Before</div>
                <div class="comparison-header-cell comparison-header-cell--after">After</div>
              </div>
              
              <div class="comparison-list">
                <div 
                  v-for="(issue, idx) in issues" 
                  :key="idx"
                  class="comparison-row"
                >
                  <div class="comparison-cell comparison-cell--info">
                    <div class="d-flex align-center">
                      <v-chip 
                        x-small 
                        color="error"
                        text-color="white"
                        class="mr-2"
                      >
                        {{ issue.hasLineBreaks ? 'LINE BREAK' : 'SPACES' }}
                      </v-chip>
                      <span class="text-body-2 mr-1">
                        <strong>Row {{ issue.rowNumber }}</strong>
                      </span>
                      <span class="grey--text text--lighten-1 mx-1">·</span>
                      <span class="text-caption grey--text">{{ issue.field }}</span>
                    </div>
                  </div>
                  
                  <div class="comparison-cell comparison-cell--before">
                    <span v-html="formatWithHighlights(issue.originalValue)"></span>
                  </div>
                  
                  <div class="comparison-cell comparison-cell--after">
                    {{ truncate(issue.cleanedValue, 100) }}
                  </div>
                </div>
              </div>
            </v-card>
            
            <div v-if="issues.length > 10" class="d-flex align-center">
              <v-btn 
                color="primary" 
                :disabled="isApplied"
                @click="applyFixes"
                large
              >
                <v-icon left>mdi-auto-fix</v-icon>
                {{ isApplied ? 'Fixed!' : 'Fix All' }}
              </v-btn>
              
              <v-spacer></v-spacer>
              
              <v-chip v-if="isApplied" color="success" text-color="white">
                <v-icon left small>mdi-check</v-icon>
                {{ issues.length }} issue{{ issues.length > 1 ? 's' : '' }} fixed
              </v-chip>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
export default {
  name: 'StepWhitespace',
  props: {
    issues: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isApplied: false
    };
  },
  computed: {
    lineBreakCount() {
      return this.issues.filter(i => i.hasLineBreaks).length;
    },
    extraSpaceCount() {
      return this.issues.filter(i => i.hasExtraSpaces && !i.hasLineBreaks).length;
    }
  },
  methods: {
    formatWithHighlights(value) {
      if (!value) return '';
      
      let result = this.escapeHtml(value);
      
      result = result
        .replace(/\r\n/g, '<span class="removed-char">↵</span>')
        .replace(/\n/g, '<span class="removed-char">↵</span>')
        .replace(/\r/g, '<span class="removed-char">↵</span>');
      
      result = result.replace(/^(\s+)/, (match) => {
        return '<span class="removed-char">' + '·'.repeat(match.length) + '</span>';
      });
      result = result.replace(/(\s+)$/, (match) => {
        return '<span class="removed-char">' + '·'.repeat(match.length) + '</span>';
      });
      
      result = result.replace(/  +/g, (match) => {
        return ' <span class="removed-char">' + '·'.repeat(match.length - 1) + '</span>';
      });
      
      if (result.length > 200) {
        result = result.substring(0, 197) + '...';
      }
      
      return result;
    },
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },
    truncate(str, len) {
      if (!str) return '';
      if (str.length <= len) return str;
      return str.substring(0, len) + '...';
    },
    applyFixes() {
      this.isApplied = true;
      this.$emit('apply-fixes', this.issues);
    }
  },
  watch: {
    issues: {
      handler() {
        this.isApplied = false;
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.issue-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.issue-icon--linebreak {
  background: linear-gradient(135deg, #ef5350, #c62828);
}

.issue-icon--space {
  background: linear-gradient(135deg,  #ef5350, #c62828);
}

.issue-summary-card {
  transition: transform 0.2s;
}

.issue-summary-card:hover {
  transform: translateY(-2px);
}

.comparison-header {
  display: flex;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  color: #616161;
}

.comparison-header-cell {
  padding: 10px 12px;
}

.comparison-header-cell--info {
  width: 220px;
  flex-shrink: 0;
}

.comparison-header-cell--before,
.comparison-header-cell--after {
  flex: 1;
  min-width: 0;
}

.comparison-list {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
}

.comparison-row {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
}

.comparison-row:last-child {
  border-bottom: none;
}

.comparison-cell {
  padding: 12px;
  word-break: break-word;
}

.comparison-cell--info {
  display: flex;
  align-items: center;
  width: 250px;
  flex-shrink: 0;
  background: #fafafa;
  font-family: inherit;
  white-space: nowrap;
}

.comparison-cell--before {
  flex: 1;
  min-width: 0;
  background-color: #ffebee;
  color: #b71c1c;
}

.comparison-cell--after {
  flex: 1;
  min-width: 0;
  background-color: #e8f5e9;
  color: #1b5e20;
}

.comparison-cell--before >>> .removed-char,
.removed-char {
  background-color: #ef9a9a;
  color: #b71c1c;
  padding: 1px 3px;
  border-radius: 2px;
  font-weight: bold;
}
</style>
